import { Checkbox, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { REQUIRED_FIXED_BOXES } from '../lib/constants';
import { LocalizationProvider, DesktopTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const useStyles = makeStyles({
  input: {
    width: '70%',
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'row',
    border: '1px solid #585858',
    margin: 8,
    justifyContent: 'flex-start',
    flexGrow: 1,
  },
  rowContainer: {
    display: 'flex',
    border: '1px solid #585858',
    margin: 4,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  inputVal: {
    height: 'auto',
    padding: '0 5px',
    fontSize: 14,
  },
  inputValTime: {
    height: 10,
    width: 50,
    fontSize: 14,
  },
});

type BoxDataType = {
  boxId: number;
  boxNameText: string;
  boxValue: number | string | undefined;
  entryId: number | undefined;
  isReadOnly: boolean;
  isFixedBox: boolean;
  isYesNoBox: boolean;
  isAddedFromTerminal: boolean;
  isTimeBox: boolean;
  isAlphaNumeric: boolean;
};

interface ValueBoxProps {
  boxData: BoxDataType;
  onValueChanged: (
    id: number,
    val: string,
    entryId: number | undefined,
  ) => void;
}

export function ValueBox({ boxData, onValueChanged }: ValueBoxProps) {
  const classes = useStyles();
  const [isTimeCorrect, setTimeCorrect] = useState<boolean>(false);
  const isAlphaNumeric =
    boxData.isAlphaNumeric === true &&
    boxData.isTimeBox !== true &&
    boxData.isYesNoBox !== true;

  const getProperTimeString = (timeValStr: string): string => {
    if (timeValStr !== '') {
      return (
        '1-1-2022 ' + timeValStr.substring(0, 2) + ':' + timeValStr.substring(2)
      );
    } else {
      return '';
    }
  };
  const getTimeStringFromNumber = (numberValue: number): string => {
    const numStr = numberValue.toString();

    if (numStr.length === 4) {
      return getProperTimeString(numStr);
    } else if (numStr.length === 3) {
      return getProperTimeString('0' + numStr);
    } else if (numStr.length === 2) {
      return '1-1-2022 00:' + numStr;
    } else if (numStr.length === 1) {
      return '1-1-2022 00:0' + numStr;
    } else {
      return '';
    }
  };

  const isTimeInput =
    !isAlphaNumeric &&
    ((boxData.isFixedBox && boxData.boxNameText !== 'Volume') ||
      boxData.isTimeBox);

  const getInitialValue = (): string => {
    if (isAlphaNumeric && (!boxData.entryId || boxData.entryId === 0)) {
      return '';
    }

    const value = boxData.boxValue;
    if (value === undefined || value === '') {
      return '';
    }
    if (!isAlphaNumeric && (value === 0 || value === '0')) {
      return '';
    }

    return isTimeInput ? getTimeStringFromNumber(Number(value)) : String(value);
  };

  const [myValue, setMyValue] = useState<string>(getInitialValue);

  useEffect(() => {
    setMyValue(getInitialValue());
  }, [
    boxData.boxValue,
    boxData.entryId,
    boxData.boxNameText,
    boxData.isFixedBox,
    boxData.isTimeBox,
    boxData.isAlphaNumeric,
  ]);
  const handleChange = (time: Date | null) => {
    if (time && time.toString() !== 'Invalid Date') {
      setTimeCorrect(time.toString().length > 10);
    } else {
      setTimeCorrect(false);
    }
    setMyValue(time ? time.toString() : '');
  };

  const isRequiredEmpty =
    REQUIRED_FIXED_BOXES.includes(boxData.boxNameText) &&
    (myValue === '' || (!isAlphaNumeric && myValue === '0'));

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onValueChanged(
      boxData.boxId,
      isTimeCorrect ? e.target.value.replace(/:/g, '') : '',
      boxData.entryId ? boxData.entryId : undefined,
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (e.target instanceof HTMLInputElement) {
        e.target.blur();
      }
      e.preventDefault();
    }
  };

  return (
    <div
      className={classes.rowContainer}
      style={isRequiredEmpty ? { border: '2px solid #d32f2f' } : undefined}
    >
      <div
        style={{
          padding: '2px 4px 2px 4px',
          backgroundColor: isRequiredEmpty ? '#ffcdd2' : '#d1d1d1',
          fontSize: 13,
        }}
      >
        {`${boxData.boxNameText}`}
      </div>

      <div
        style={{
          width: boxData.isYesNoBox ? 40 : 60,
          backgroundColor: '#ffffff',
          fontSize: 13,
          marginLeft: boxData.isYesNoBox ? 0 : 6,
        }}
      >
        {isTimeInput ? (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopTimePicker
              value={myValue === '' ? null : myValue}
              onChange={handleChange}
              ampm={false}
              mask="__:__"
              disableOpenPicker={true}
              OpenPickerButtonProps={{
                disabled: true,
                style: { display: 'none' },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="00:00"
                  InputProps={{
                    ...params.InputProps,
                    classes: { input: classes.inputValTime },
                    disableUnderline: true,
                    maxRows: 1,
                  }}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  error={Boolean(params.error)}
                  helperText={params.error ? 'Invalid' : ''}
                  variant="standard"
                />
              )}
            />
          </LocalizationProvider>
        ) : boxData.isYesNoBox ? (
          <Checkbox
            name={`YN-${boxData.boxId}`}
            size="small"
            disabled={boxData.isReadOnly}
            checked={myValue === 'Y'}
            style={{
              paddingRight: 2,
              paddingLeft: 2,
              paddingTop: 1,
              paddingBottom: 1,
              color: '#68bc46',
            }}
            onChange={(e) => {
              onValueChanged(
                boxData.boxId,
                e.target.checked ? 'Y' : 'N',
                boxData.entryId ? boxData.entryId : undefined,
              );
              setMyValue(e.target.checked ? 'Y' : 'N');
            }}
          />
        ) : (
          <TextField
            variant="standard"
            id={`T${boxData.boxId}`}
            name={`T${boxData.boxId}`}
            disabled={boxData.isReadOnly && !isAlphaNumeric}
            autoComplete="off"
            InputProps={{
              classes: { input: classes.inputVal },
              disableUnderline: true,
              maxRows: 1,
            }}
            sx={{
              height: 'auto',
              padding: '0 5px',
            }}
            inputProps={{ maxLength: isAlphaNumeric ? 50 : 5 }}
            value={myValue}
            onBlur={(e) => {
              onValueChanged(
                boxData.boxId,
                e.target.value,
                boxData.entryId ? boxData.entryId : undefined,
              );
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                if (e.target && e.target instanceof HTMLInputElement) {
                  (e.target as HTMLInputElement).blur();
                }
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              if (
                e.target &&
                e.target.value !== null &&
                e.target.value !== undefined
              ) {
                const value = isAlphaNumeric
                  ? e.target.value.replace(/[^a-zA-Z0-9]/g, '')
                  : e.target.value.replace(/[^0-9]/g, '');
                setMyValue(value);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
